import React, { ReactNode, useEffect, useState } from 'react';
import Box from 'ui-box';
import { IInputGroupProps, InputGroup } from './InputGroup';
import { Menu } from './Menu';
import { Popover } from './Popover';
import { TagInput } from './TagInput';

interface Identifiable {
  id: number | string;
}

export interface ISuggestProps<T extends Identifiable>
  extends IInputGroupProps {
  items?: T[];
  itemRenderer: (item: T) => ReactNode;
  onSelectItem: (item: T) => void;
}

export const Suggest = React.forwardRef<HTMLInputElement, ISuggestProps<any>>(
  ({ items, itemRenderer, onSelectItem, ...inputProps }, ref) => {
    const [focussed, setFocussed] = useState(false);
    const [innerValue, setInnerValue] = useState(
      inputProps.value || inputProps.defaultValue
    );

    return (
      <Popover
        isOpen={focussed && items && items.length > 0}
        canSelfClose={false}
        content={() => (
          <Menu>
            {(items || [])
              .filter(
                (i) =>
                  JSON.stringify(i)
                    .toLowerCase()
                    .indexOf(
                      innerValue ? (innerValue + '').toLowerCase() : ''
                    ) > -1
              )
              .map((item) => (
                <Box
                  key={item.id}
                  className="item bordered"
                  display="flex"
                  alignItems="baseline"
                  onClick={() => {
                    onSelectItem(item);
                  }}
                >
                  {itemRenderer(item)}
                </Box>
              ))}
          </Menu>
        )}
      >
        <InputGroup
          {...inputProps}
          onBlur={(e) => {
            setFocussed(false);
            inputProps.onBlur && inputProps.onBlur(e);
          }}
          onFocus={(e) => {
            setFocussed(true);
            inputProps.onFocus && inputProps.onFocus(e);
          }}
          ref={ref}
          onChange={(e) => {
            setInnerValue(e.target.value);
            inputProps.onChange && inputProps.onChange(e);
          }}
        />
      </Popover>
    );
  }
);

export const TagSuggest = React.forwardRef<
  HTMLInputElement,
  Omit<Omit<Omit<ISuggestProps<any>, 'onChange'>, 'value'>, 'defaultValue'> & {
    onChange?: (v: string[]) => void;
    value?: string[];
    defaultValue?: string[];
  }
>(({ items, itemRenderer, onSelectItem, ...inputProps }, ref) => {
  const [focussed, setFocussed] = useState(false);
  const [innerValue, setInnerValue] = useState<string[] | undefined>(
    inputProps.value || inputProps.defaultValue
  );

  useEffect(() => {
    setInnerValue(inputProps.value);
  }, [inputProps.value]);

  return (
    <Popover
      isOpen={focussed && items && items.length > 0}
      canSelfClose={false}
      content={() => (
        <Menu>
          {(items || []).map((item, i) => (
            <Box
              key={item.id || i}
              className="item bordered"
              display="flex"
              alignItems="baseline"
              onClick={() => {
                onSelectItem(item);
              }}
            >
              {itemRenderer(item)}
            </Box>
          ))}
        </Menu>
      )}
    >
      <TagInput
        value={innerValue}
        onBlur={(e) => {
          setFocussed(false);
          inputProps.onBlur && inputProps.onBlur(e);
        }}
        onFocus={(e) => {
          setFocussed(true);
          inputProps.onFocus && inputProps.onFocus(e);
        }}
        ref={ref}
        onChange={(v) => {
          setInnerValue(v);
          inputProps.onChange && inputProps.onChange(v);
        }}
        placeholder={inputProps.placeholder}
        validationMessage={inputProps.validationMessage}
        isInvalid={inputProps.isInvalid}
      />
    </Popover>
  );
});
